import React, { FC, useState } from 'react';
import { Empty, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Redirect, Route, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { PercentageOutlined } from '@ant-design/icons';
import { set } from 'lodash';
import { UseQueryResult } from 'react-query';
import NoteText from '~/components/NoteText';
import ApplicationCommonErrors from '~/components/views/ApplicationCommonErrors';
import ReviewAndSubmitRightContent from '~/components/views/ReviewAndSubmit/ReviewAndSubmitRightContent';
import features from '~/constants/features';
import { useGetApplication, useSetSalesForceApplicationData } from '~/controllers/wizard';
import ApplicationNavigation from '~/layouts/ApplicationNavigation';
import BottomLeftActions from '~/layouts/BottomLeftActions';
import LeftLayout from '~/layouts/LeftLayout';
import WizardContent from '~/layouts/WizardContent';
import WizardContinueButton from '~/layouts/WizardContinueButton';
import paths from '~/pages/paths';
import { getStagePayload } from '~/utils/getStagePayload';
import UaePassImportDocumentAlert from '~/components/views/authorizedRepresentative/uaePassImportDocument/UaePassImportAlert';
import theme from '~/constants/theme';
import { ApiError } from '~/types/ApiError';
import { SalesForceRefreshPayload } from '~/types/ApplicationPayload';
import { Application as ApplicationType } from '~/types/Application';
import { SalesForceRefreshParams } from '~/types/ApplicationStep';
import { useGetDraft } from '~/controllers/draft';
import additionalInformation from './additionalInformation';
import addresses from './addresses';
import authorizedRepresentative from './authorizedRepresentative';
import bankAccount from './bankAccount';
import companyInformation from './companyInformation';
import reviewAndSubmit from './reviewAndSubmit';
import shareholders from './shareholders';

const views: Record<string, React.ComponentType> = {
  companyInformation,
  addresses,
  authorizedRepresentative,
  shareholders,
  additionalInformation,
  bankAccount,
  reviewAndSubmit,
};
const Application: FC<void> = () => {
  const { t } = useTranslation('fillApplication');
  const location = useLocation();
  const applicationQuery = useGetApplication();
  const { data }: UseQueryResult<ApplicationType, ApiError> = applicationQuery;

  const stagePayload = getStagePayload.wizard(data);
  const applicationSteps = stagePayload?.steps.find((item) => item.view === 'application')?.steps;

  const isReviewAndSubmit = location.pathname.includes('reviewAndSubmit');
  const isAuthorizedRepresentative = location.pathname.includes('authorizedRepresentative');
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.lg });

  return (
    <WizardContent
      left={
        <LeftLayout>
          <ApplicationNavigation />
          <BottomLeftActions contactUsUrl={features.contactUsLinkWizardApplication} />
        </LeftLayout>
      }
      content={
        <>
          {applicationSteps?.[0] && paths.wizard.application === location.pathname && (
            <Redirect
              exact
              from={paths.wizard.application}
              to={`${paths.wizard.application}/${applicationSteps?.[0].uiKey}`}
            />
          )}
          {applicationSteps?.map((item) => {
            if (item.state === 'HIDDEN') return null;
            return (
              <Route
                key={item.key}
                path={`${paths.wizard.application}/${item.uiKey}` as any}
                component={views[item.uiKey] || Empty}
              />
            );
          })}
        </>
      }
      right={
        isReviewAndSubmit ? (
          <ReviewAndSubmitRightContent />
        ) : (
          <RightContainer>
            <div className="upperContainer">{!isTablet && <UaePassImportDocumentAlert />}</div>
            <ApplicationCommonErrors />
            {isAuthorizedRepresentative && (
              <NoteText>{t('Please note, any individual provided in this section will be screened.')}</NoteText>
            )}
            <WizardContinueButton id="applicationSteps_continueButton" formName={location.pathname} />
          </RightContainer>
        )
      }
    />
  );
};

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: flex-end;

  .upperContainer {
    height: 100%;
  }

  .WizardContinueButton,
  .NoteText {
    margin-top: 8px;
  }

  .refreshButton {
    color: black;
    background: white;
    :hover {
      color: white;
      border-color: #ea687f;
      background: #ea687f;
    }
  }
`;

export default Application;
